import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTree, MatTreeNodeDef, MatTreeNode, MatNestedTreeNode, MatTreeNodeToggle, MatTreeNodeOutlet } from '@angular/material/tree';
import { formatDate } from '@angular/common';
import { Subject, BehaviorSubject } from 'rxjs';
import { orderBy, groupBy } from 'lodash-es';
import { OrderUtilityService } from '../../../../services';
import { OrderControllerService } from '../../../../services/order/order-controller.service';
import { OrderShipmentGroup, OrderShipment } from './../../../../models';
import { OrderFilter } from './../../../../models';
import { OrderStatusNode } from '../../models/interfaces';
import { LineStatus, ErrorType } from './../../../../models/enums';
import { takeUntil } from 'rxjs/operators';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FiltersComponent } from '../../../../shared/components/filters/filters.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { OrderShipmentGroupsList } from '../../../../models/order-shipment-groups-list';

@Component({
  selector: 'insight-errors-by-ship-date',
  templateUrl: './errors-by-ship-date.component.html',
  styleUrls: ['./errors-by-ship-date.component.scss', '../../scss/operational-dashboard-shared.scss'],
  imports: [RouterLink, MatButton, MatIcon, FiltersComponent, LoadingSpinnerComponent, MatTree, MatTreeNodeDef, MatTreeNode, MatIconButton, MatNestedTreeNode, MatTreeNodeToggle, MatTreeNodeOutlet]
})
export class ErrorsByShipDateComponent implements OnInit, OnDestroy {
  public nodeDataSource: OrderStatusNode[] = [];
  public childrenAccessor = (node: OrderStatusNode) => node.children ?? [];
  public isLoading = true;
  public didError = false;
  public filtersSubject: BehaviorSubject<OrderFilter> = new BehaviorSubject<OrderFilter>(new OrderFilter());

  @ViewChild(MatTree) private tree: MatTree<OrderStatusNode>;

  private searchText = '';
  private orderShipmentGroupsListCopy: OrderShipmentGroupsList;
  private filtersCopy: OrderFilter;
  private unsubscribeTrigger$ = new Subject();

  constructor(
    private orderControllerService: OrderControllerService,
    private orderUtilityService: OrderUtilityService) { }

  ngOnInit(): void {
    this.orderControllerService.orderDataRefreshedEvent
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe(() => {
        if (this.isLoading || this.orderControllerService.orderDataNeedsRefresh()) {
          this.orderShipmentGroupsListCopy = this.orderControllerService.getAllOrderShipments();
          this.nodeDataSource = this.generateNodeData(this.orderShipmentGroupsListCopy.orderShipmentGroups);
          this.isLoading = false;
        }
      });

    this.filtersSubject
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe(filters => {
        this.filtersCopy = filters;
        const filteredOrders = this.orderUtilityService.applyFilters(this.orderShipmentGroupsListCopy, filters, this.searchText);
        this.nodeDataSource = this.generateNodeData(filteredOrders);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeTrigger$.next();
    this.unsubscribeTrigger$.complete();
  }

  public collapseAll() {
    this.tree.collapseAll();
  }

  public expandAll() {
    this.tree.expandAll();
  }

  public hasChild(notUsed: number, node: OrderStatusNode): boolean {
    return !!node.children && node.children.length > 0;
  }

  public hasData(): boolean {
    return this.orderShipmentGroupsListCopy.orderShipmentGroups.length > 0;
  }

  private generateNodeData(orderShipmentGroups: OrderShipmentGroup[]): OrderStatusNode[] {
    let allOrderShipments: OrderShipment[] = [];

    if (orderShipmentGroups) {
      orderShipmentGroups.forEach(group => {
        if (group.status !== LineStatus.Complete) {
          allOrderShipments = allOrderShipments.concat(group.orderShipments);
        }
      });
    }

    const allOrderShipmentsSorted = orderBy(allOrderShipments, 'shipDate', 'asc');

    return this.createErrorDataNodes(allOrderShipmentsSorted);
  }

  private createErrorDataNodes(orderShipments: OrderShipment[]): OrderStatusNode[] {
    const orderStatusNodes: OrderStatusNode[] = [];
    const shipmentsWithErrors = orderShipments.filter(x => x.hasError);
    const errorDateGroups = groupBy(shipmentsWithErrors, 'shipDate');

    Object.keys(errorDateGroups).forEach(key => {
      const shipDateQueryParam = this.orderUtilityService.dateCompareToToday(key);

      let errorCount = 0;
      errorDateGroups[key].forEach((shipment: OrderShipment) => {
        shipment.errors.forEach(e => {
          if (this.filtersCopy) {
            if (this.filtersCopy.errorFilter.includes(e.type) || this.filtersCopy.errorFilter.includes(ErrorType.All)) {
              errorCount++;
            }
          } else {
            errorCount++;
          }
        });
      });
      const node: OrderStatusNode = {
        name: `Errors on orders shipping ${formatDate(key, 'MMM d, y', 'en-US')}`,
        nodeMatchesCount: errorCount,
        queryParams: { minShip: shipDateQueryParam, maxShip: shipDateQueryParam, errorsOnly: true },
        children: this.createErrorTypeNodes(errorDateGroups[key], shipDateQueryParam)
      };
      orderStatusNodes.push(node);
    });

    return orderStatusNodes;
  }

  private createErrorTypeNodes(orderShipments: OrderShipment[], shipDateQueryParam: number): OrderStatusNode[] {
    const orderStatusNodes: OrderStatusNode[] = [];
    const errorTypeRecord: Record<ErrorType, OrderShipment[]> = {
      0: [], 1: [], 2: [], 3: [], 4: [],
      5: [], 6: [], 7: [], 8: [], 9: []
    };

    orderShipments.forEach(s => {
      s.errors.forEach(e => {
        errorTypeRecord[e.type].push(s);
      });
    });

    Object.keys(errorTypeRecord).forEach(key => {
      if (errorTypeRecord[key].length > 0) {
        if (this.filtersCopy) {
          if (this.filtersCopy.errorFilter.includes(parseInt(key, 10)) || this.filtersCopy.errorFilter.includes(ErrorType.All)) {
            const node: OrderStatusNode = {
              name: this.orderUtilityService.mapOrderErrorType(parseInt(key, 10)),
              nodeMatchesCount: errorTypeRecord[key].length,
              queryParams: { errorType: key, minShip: shipDateQueryParam, maxShip: shipDateQueryParam, errorsOnly: true },
            };
            orderStatusNodes.push(node);
          }
        } else {
          const node: OrderStatusNode = {
            name: this.orderUtilityService.mapOrderErrorType(parseInt(key, 10)),
            nodeMatchesCount: errorTypeRecord[key].length,
            queryParams: { errorType: key, minShip: shipDateQueryParam, maxShip: shipDateQueryParam, errorsOnly: true },
          };
          orderStatusNodes.push(node);
        }
      }
    });

    return orderStatusNodes;
  }
}
